<!-- src/components/MealsBoardView.vue -->
<script setup lang="ts">
import { computed, ref } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { CREATE_MEAL, DELETE_MEAL } from '../graphql/mutations'
import { GET_MEALS_BY_DATE_RANGE } from '../graphql/queries'
import { showError, showSuccess } from '../utils/errorHandling'
import { keyToUtcIso } from '../utils/dateHelpers'
import { getMealTypeColor } from '../utils/mealTypeColors'
import BoardRecipePicker from './BoardRecipePicker.vue'
import type { Meal, MealType } from '../graphql/types'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

const props = defineProps<{ mealTypes: MealType[] }>()

const emit = defineEmits<{
  'edit-meal': [meal: Meal]
}>()

const weekStart = ref(dayjs.utc().startOf('week').add(1, 'day'))
const selectedDayKey = ref(dayjs.utc().format('YYYY-MM-DD'))
const dragOverCellKey = ref<string | null>(null)
const showPicker = ref(false)
const pickerTarget = ref<{ dateKey: string; typeId: string } | null>(null)

const { mutate: createMeal } = useMutation(CREATE_MEAL)
const { mutate: deleteMeal } = useMutation(DELETE_MEAL)

const weekDays = computed(() => {
  const todayKey = dayjs.utc().format('YYYY-MM-DD')
  return Array.from({ length: 7 }, (_, i) => {
    const d = weekStart.value.add(i, 'day')
    const dateKey = d.format('YYYY-MM-DD')
    return {
      dateKey,
      dow: d.format('ddd').toUpperCase(),
      dayNumber: d.format('D'),
      isToday: dateKey === todayKey,
    }
  })
})

const weekRangeLabel = computed(() => {
  const start = dayjs.utc(weekDays.value[0].dateKey)
  const end = dayjs.utc(weekDays.value[6].dateKey)
  const startLabel = start.format('MMM D')
  const endLabel = start.isSame(end, 'month') ? end.format('D') : end.format('MMM D')
  return `${startLabel} – ${endLabel}, ${end.format('YYYY')}`
})

const gridTemplateStyle = computed(() => ({
  gridTemplateColumns: `96px repeat(${props.mealTypes.length}, minmax(160px, 1fr))`,
}))

const {
  result: boardMealsResult,
  loading: boardLoading,
  error: boardError,
  refetch: refetchBoardMeals,
} = useQuery(GET_MEALS_BY_DATE_RANGE, () => ({
  start_date: dayjs.utc(weekDays.value[0].dateKey).format('DD/MM/YYYY'),
  end_date: dayjs.utc(weekDays.value[6].dateKey).format('DD/MM/YYYY'),
  show_active: false,
}))

const meals = computed(() => boardMealsResult.value?.mealsByDateRange || [])

const mealDateKeys = (meal: Meal): string[] => {
  const dates = meal.scheduled_dates?.length ? meal.scheduled_dates : [meal.scheduled_date]
  return dates.map((d) => dayjs.utc(d).format('YYYY-MM-DD'))
}

const cellKey = (dateKey: string, typeId: string) => `${dateKey}__${typeId}`

const mealsByCell = computed(() => {
  const map: Record<string, Meal[]> = {}
  for (const meal of meals.value) {
    for (const dateKey of mealDateKeys(meal)) {
      const key = cellKey(dateKey, meal.type_id)
      if (!map[key]) map[key] = []
      map[key].push(meal)
    }
  }
  return map
})

const mealsForCell = (dateKey: string, typeId: string) => mealsByCell.value[cellKey(dateKey, typeId)] || []

const daysWithMeals = computed(() => {
  const set = new Set<string>()
  for (const meal of meals.value) {
    for (const dateKey of mealDateKeys(meal)) set.add(dateKey)
  }
  return set
})

const hasMealsOnDay = (dateKey: string) => daysWithMeals.value.has(dateKey)

const isWeekEmpty = computed(() => weekDays.value.every((day) => !hasMealsOnDay(day.dateKey)))

const prevWeek = () => { weekStart.value = weekStart.value.subtract(7, 'day') }
const nextWeek = () => { weekStart.value = weekStart.value.add(7, 'day') }

const onDragOver = (dateKey: string, typeId: string) => {
  dragOverCellKey.value = cellKey(dateKey, typeId)
}

const onDragLeave = (e: DragEvent) => {
  const related = e.relatedTarget as HTMLElement | null
  if (!related || !(e.currentTarget as HTMLElement).contains(related)) {
    dragOverCellKey.value = null
  }
}

const createQuickMeal = async (recipe: { id: string; title: string }, dateKey: string, typeId: string) => {
  try {
    await createMeal({
      title: recipe.title,
      recipe_id: recipe.id,
      type_id: typeId,
      scheduled_date: keyToUtcIso(dateKey),
      scheduled_dates: [keyToUtcIso(dateKey)],
      portions_days: 1,
    })
    showSuccess('Meal added to board!')
    refetchBoardMeals()
  } catch (err) {
    showError(err, 'add meal to board')
  }
}

const onDrop = async (e: DragEvent, dateKey: string, typeId: string) => {
  dragOverCellKey.value = null
  const raw = e.dataTransfer?.getData('text/plain')
  if (!raw) return

  let recipe: { id: string; title: string }
  try {
    recipe = JSON.parse(raw)
  } catch {
    return
  }
  if (!recipe?.id || !recipe?.title) return

  await createQuickMeal(recipe, dateKey, typeId)
}

const openPicker = (dateKey: string, typeId: string) => {
  pickerTarget.value = { dateKey, typeId }
  showPicker.value = true
}

const onPickerSelectRecipe = async (recipe: { id: string; title: string }) => {
  if (!pickerTarget.value) return
  await createQuickMeal(recipe, pickerTarget.value.dateKey, pickerTarget.value.typeId)
  showPicker.value = false
  pickerTarget.value = null
}

const removeMeal = async (meal: Meal) => {
  try {
    await deleteMeal({ id: meal.id })
    showSuccess('Meal removed from board!')
    refetchBoardMeals()
  } catch (err) {
    showError(err, 'remove meal from board')
  }
}
</script>

<template>
  <div class="board-layout">
    <div class="board-sidebar">
      <BoardRecipePicker mode="sidebar" />
    </div>

    <div class="board-main card-elevated">
      <div v-if="boardLoading" class="board-state">
        <div class="board-spinner"></div>
        <p class="board-state-text">Loading your board…</p>
      </div>

      <div v-else-if="boardError" class="board-state">
        <svg class="board-state-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m0 3h.008v.008H12v-.008zM21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 class="board-state-title">Couldn't load your board</h3>
        <p class="board-state-text">Please try again or check your connection.</p>
        <button class="board-retry-btn" @click="refetchBoardMeals()">Try Again</button>
      </div>

      <template v-else>
        <div class="board-week-nav">
          <button class="board-nav-btn" aria-label="Previous week" @click="prevWeek">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h3 class="board-week-label">{{ weekRangeLabel }}</h3>
          <button class="board-nav-btn" aria-label="Next week" @click="nextWeek">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <div class="board-day-strip">
          <button
            v-for="day in weekDays"
            :key="day.dateKey"
            class="board-day-chip"
            :class="{ 'board-day-chip--selected': day.dateKey === selectedDayKey }"
            :aria-pressed="day.dateKey === selectedDayKey"
            :aria-label="`${day.dow} ${day.dayNumber}${hasMealsOnDay(day.dateKey) ? ', has meals planned' : ''}`"
            @click="selectedDayKey = day.dateKey"
          >
            <span class="board-day-dow">{{ day.dow }}</span>
            <span class="board-day-number">{{ day.dayNumber }}</span>
            <span v-if="hasMealsOnDay(day.dateKey)" class="board-day-dot"></span>
          </button>
        </div>

        <p v-if="isWeekEmpty" class="board-empty-banner">
          Your week is empty. Drag a recipe from the library, or tap "+ Add recipe", to get started.
        </p>

        <div class="board-grid">
          <div class="board-grid-header" :style="gridTemplateStyle">
            <div></div>
            <div v-for="type in props.mealTypes" :key="type.id" class="board-col-header">
              <span class="board-type-dot" :style="{ backgroundColor: getMealTypeColor(type.name) }"></span>
              {{ type.name }}
            </div>
          </div>

          <div v-for="day in weekDays" :key="day.dateKey" class="board-row" :style="gridTemplateStyle">
            <div class="board-row-label">
              <span class="board-row-dow">{{ day.dow }}</span>
              <span class="board-row-date">{{ day.dayNumber }}</span>
            </div>

            <div
              v-for="type in props.mealTypes"
              :key="type.id"
              class="board-cell"
              :class="{ 'board-cell--drag-over': dragOverCellKey === cellKey(day.dateKey, type.id) }"
              @dragover.prevent="onDragOver(day.dateKey, type.id)"
              @dragleave="onDragLeave"
              @drop.prevent="onDrop($event, day.dateKey, type.id)"
            >
              <div v-if="mealsForCell(day.dateKey, type.id).length === 0" class="board-drop-hint">Drop here</div>
              <div v-else class="board-chips">
                <div
                  v-for="meal in mealsForCell(day.dateKey, type.id)"
                  :key="meal.id"
                  class="board-chip"
                  :style="{ borderLeftColor: getMealTypeColor(type.name) }"
                  @click="emit('edit-meal', meal)"
                >
                  <span class="board-chip-title">{{ meal.title }}</span>
                  <button class="board-chip-remove" :aria-label="`Remove ${meal.title}`" @click.stop="removeMeal(meal)">×</button>
                </div>
              </div>
              <button
                class="board-cell-add-btn"
                :aria-label="`Add recipe to ${type.name} on ${day.dow} ${day.dayNumber}`"
                @click="openPicker(day.dateKey, type.id)"
              >+</button>
            </div>
          </div>
        </div>

        <div class="board-mobile-day">
          <div v-for="type in props.mealTypes" :key="type.id" class="board-mobile-section">
            <div class="board-mobile-section-header">
              <span class="board-type-dot" :style="{ backgroundColor: getMealTypeColor(type.name) }"></span>
              <span class="board-mobile-section-title">{{ type.name }}</span>
            </div>
            <div
              v-for="meal in mealsForCell(selectedDayKey, type.id)"
              :key="meal.id"
              class="board-chip board-chip--mobile"
              :style="{ borderLeftColor: getMealTypeColor(type.name) }"
              @click="emit('edit-meal', meal)"
            >
              <span class="board-chip-title">{{ meal.title }}</span>
              <button class="board-chip-remove" :aria-label="`Remove ${meal.title}`" @click.stop="removeMeal(meal)">×</button>
            </div>
            <button class="board-add-recipe-btn" @click="openPicker(selectedDayKey, type.id)">+ Add recipe</button>
          </div>
        </div>
      </template>
    </div>

    <BoardRecipePicker mode="sheet" :open="showPicker" @select-recipe="onPickerSelectRecipe" @close="showPicker = false" />
  </div>
</template>

<style scoped>
.board-layout {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 24px;
  align-items: start;
}

.board-sidebar { position: sticky; top: 16px; }

.board-main {
  background: white;
  border-radius: 20px;
  overflow: hidden;
}

.board-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 64px 24px;
}
.board-spinner {
  width: 40px;
  height: 40px;
  border-radius: 9999px;
  border: 3px solid #E2E8F0;
  border-bottom-color: #4CAF50;
  animation: board-spin 700ms linear infinite;
  margin-bottom: 16px;
}
@keyframes board-spin { to { transform: rotate(360deg); } }
.board-state-icon { width: 40px; height: 40px; color: #CBD5E1; margin-bottom: 12px; }
.board-state-title { font-family: Inter, sans-serif; font-weight: 800; font-size: 16px; color: #0F172A; margin-bottom: 6px; }
.board-state-text { font-size: 13px; color: #64748B; max-width: 320px; }
.board-retry-btn {
  margin-top: 16px;
  height: 40px;
  padding: 0 20px;
  border-radius: 9999px;
  border: none;
  background: #4F8646;
  color: white;
  font-family: Inter, sans-serif;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
}
.board-retry-btn:hover { opacity: 0.9; }

.board-empty-banner {
  margin: 12px 16px 0;
  padding: 10px 14px;
  border-radius: 10px;
  background: #F8FAFC;
  border: 1px dashed #E2E8F0;
  color: #64748B;
  font-size: 12px;
  text-align: center;
}

.board-week-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px;
  border-bottom: 1px solid #F1F5F9;
}
.board-week-label { font-family: Inter, sans-serif; font-weight: 800; font-size: 15px; color: #0F172A; }
.board-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #E2E8F0;
  background: white;
  color: #64748B;
  cursor: pointer;
  transition: background-color 150ms ease, color 150ms ease;
}
.board-nav-btn:hover { background-color: #F1F5F9; color: #0F172A; }

.board-day-strip {
  display: none;
  gap: 8px;
  padding: 12px 16px;
  overflow-x: auto;
  border-bottom: 1px solid #F1F5F9;
}
.board-day-chip {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  width: 56px;
  padding: 8px 0;
  border-radius: 12px;
  border: none;
  background: #F1F5F9;
  cursor: pointer;
  position: relative;
  transition: background-color 150ms ease, color 150ms ease;
}
.board-day-chip--selected { background: #4F8646; }
.board-day-dow { font-size: 10px; font-weight: 700; color: #94A3B8; letter-spacing: 0.04em; }
.board-day-chip--selected .board-day-dow { color: rgba(255, 255, 255, 0.85); }
.board-day-number { font-size: 16px; font-weight: 800; color: #0F172A; }
.board-day-chip--selected .board-day-number { color: white; }
.board-day-dot { width: 4px; height: 4px; border-radius: 9999px; background: #4F8646; }
.board-day-chip--selected .board-day-dot { background: white; }

.board-grid { overflow-x: auto; }
.board-grid-header,
.board-row { display: grid; align-items: stretch; }
.board-grid-header {
  padding: 12px 16px;
  background: #F8FAFC;
  border-bottom: 1px solid #F1F5F9;
}
.board-col-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
}
.board-type-dot { width: 8px; height: 8px; border-radius: 9999px; flex-shrink: 0; }

.board-row { padding: 12px 16px; border-bottom: 1px solid #F1F5F9; gap: 12px; }
.board-row:last-child { border-bottom: none; }
.board-row-label { display: flex; flex-direction: column; justify-content: center; }
.board-row-dow { font-size: 10px; font-weight: 700; color: #94A3B8; letter-spacing: 0.04em; }
.board-row-date { font-size: 18px; font-weight: 800; color: #0F172A; }

.board-cell {
  position: relative;
  min-height: 44px;
  border-radius: 10px;
  padding: 4px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  transition: background-color 150ms ease, outline-color 150ms ease;
}
.board-cell-add-btn {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: none;
  background: white;
  color: #4F8646;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  opacity: 0;
  transition: opacity 150ms ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}
.board-cell:hover .board-cell-add-btn,
.board-cell-add-btn:focus-visible {
  opacity: 1;
}
.board-cell--drag-over {
  outline: 2px dashed rgba(79, 134, 70, 0.8);
  outline-offset: -2px;
  background-color: rgba(79, 134, 70, 0.08);
}

.board-drop-hint {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px dashed #E2E8F0;
  border-radius: 8px;
  font-size: 12px;
  color: #CBD5E1;
  min-height: 36px;
}

.board-chips { display: flex; flex-direction: column; gap: 6px; }
.board-chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: #F8FAFC;
  border-left: 3px solid #4F8646;
  border-radius: 6px;
  padding: 6px 8px;
  cursor: pointer;
  transition: background-color 150ms ease;
}
.board-chip:hover { background: #F1F5F9; }
.board-chip-title {
  font-size: 12px;
  font-weight: 700;
  color: #0F172A;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.board-chip-remove {
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: #94A3B8;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
}
.board-chip-remove:hover { color: #EF4444; }

.board-mobile-day { display: none; }
.board-mobile-section {
  padding: 16px;
  border-bottom: 1px solid #F1F5F9;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.board-mobile-section:last-child { border-bottom: none; }
.board-mobile-section-header { display: flex; align-items: center; gap: 8px; }
.board-mobile-section-title { font-family: Inter, sans-serif; font-weight: 800; font-size: 14px; color: #0F172A; }
.board-add-recipe-btn {
  height: 44px;
  border-radius: 10px;
  border: 1.5px dashed #C8DCC5;
  background: transparent;
  color: #4F8646;
  font-family: Inter, sans-serif;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 150ms ease;
}
.board-add-recipe-btn:hover { background-color: rgba(79, 134, 70, 0.06); }

@media (max-width: 1023px) {
  .board-sidebar { display: none; }
  .board-layout { grid-template-columns: 1fr; }
  .board-grid { display: none; }
  .board-mobile-day { display: flex; flex-direction: column; }
  .board-day-strip { display: flex; }
}
</style>
